import { useCallback, useMemo, useRef } from 'react';
import { useSetFieldValue } from '@s20/components/forms/FormContext';
import { Modal } from '../../../../../common/Modal';
import { DraggableResourceTypes } from '../../../../../dispatch/Dispatch';
import { ReplaceJobAssignmentBody } from './ReplaceJobAssignmentBody';

export const useReplaceJobAssignment = (options) => {
    const {
        day, job, jobAssignmentId, resourceType, resourceName, equipment = [],
    } = options || {};

    const setFieldValue = useSetFieldValue();

    // Distinguishes a genuine Cancel from a successful Save (Modal fires onClose on both).
    const savedRef = useRef(false);

    const dayAssignments = job?.assignments?.[day];
    const jobAssignment = useMemo(() =>
        dayAssignments && [...dayAssignments.staff, ...dayAssignments.equipment].find((asn) => asn.jobAssignmentId === jobAssignmentId),
        [dayAssignments, jobAssignmentId],
    );

    // Everything else riding on the slot being replaced — e.g. a flagger's work truck(s), or
    // anything mounted on those trucks to any depth — so the picker can treat them all as freed too.
    const getLinkedAssignmentIds = useCallback((resourceId) => {
        if (!resourceId) {
            return [];
        }

        const isRoot = resourceType === DraggableResourceTypes.Staff
            ? (eq) => eq.assignedEmployee?.id === resourceId
            : (eq) => eq.assignedEquipment?.id === resourceId;

        const linked = equipment.filter(isRoot);
        const linkedEquipmentIds = new Set(linked.map((eq) => eq.assignment?.id).filter((id) => id != null));

        // Equipment can be mounted on equipment (e.g. an arrow board on a truck, something on
        // that arrow board, and so on) to any depth, and any number of items can share a mount
        // point — keep expanding until a pass finds nothing new.
        let addedMore = true;
        while (addedMore) {
            addedMore = false;

            equipment.forEach((eq) => {
                if (linked.includes(eq) || !linkedEquipmentIds.has(eq.assignedEquipment?.id)) {
                    return;
                }

                linked.push(eq);
                addedMore = true;

                if (eq.assignment?.id != null) {
                    linkedEquipmentIds.add(eq.assignment.id);
                }
            });
        }

        return linked.map((eq) => eq.jobAssignmentId);
    }, [resourceType, equipment]);

    const handleReplaceJobAssignment = useCallback(() => {
        // Capture the slot up front so we can re-open the picker on it after Save.
        const groupToReplace = jobAssignment;

        // The resource being replaced shouldn't be pickable as its own replacement, and the
        // slot stays collapsed while it still shows this resource — capture its id now.
        const originalResourceId = jobAssignment?.assignment?.id;

        savedRef.current = false;

        // Collapse the card to its empty slot purely at the render layer. Nothing is persisted
        // until the replacement is assigned, so the original assignment stays intact until then.
        setFieldValue('replacingAssignmentId', jobAssignmentId);

        Modal.show({
            title: `Replace ${resourceName}`,
            initialValues: {
                replacementReason: '',
            },
            SaveButtonProps: {
                label: 'Continue',
                color: 'primary',
            },
            onClose: () => {
                // Modal fires onClose on every close, including after a successful submit.
                // Only put the original assignment back on a genuine cancel.
                if (!savedRef.current) {
                    setFieldValue('replacingAssignmentId', null);
                }
            },
            onSubmit: (submittedValues) => {
                const replacementReason = submittedValues?.replacementReason?.trim() || '';

                // The textarea is `required`, so FormValidated normally blocks an empty
                // submit; this is a defensive guard.
                if (!replacementReason) {
                    return false;
                }

                savedRef.current = true;

                // Carry the replacement context into the resource picker's assign action, which
                // performs the whole replace in one call. replacingAssignmentId stays set so the
                // slot stays collapsed until the replacement is assigned.
                setFieldValue('replacementAssignmentId', jobAssignmentId);
                setFieldValue('replacementReason', replacementReason);
                setFieldValue('replacementResourceType', resourceType);
                setFieldValue('replacementOriginalResourceId', originalResourceId);
                setFieldValue('replacementLinkedAssignmentIds', [jobAssignmentId, ...getLinkedAssignmentIds(originalResourceId)]);

                // Open the existing resource picker on the now-collapsed slot.
                if (groupToReplace) {
                    setFieldValue('selectedResourceType', resourceType);
                    setFieldValue('selectedResourceGroup', groupToReplace);
                }

                return true;
            },
            body: ReplaceJobAssignmentBody,
        });
    }, [jobAssignment, resourceName, resourceType, jobAssignmentId, setFieldValue, getLinkedAssignmentIds]);

    return handleReplaceJobAssignment;
};
